import type {
  EventWorldScope,
  LiveTastingView,
  MemberType,
  RsvpSummaryView,
  WineCategoryInput,
} from '@gwc/shared';
import { api } from '../lib/api';
import type { EventRow, WineRow } from './models';

/** Tier 2 setup fields shared by create and update (docs/adr/002-tier2-contract.md). */
interface EventSetupInput {
  /** Old World, New World, or both (the member picks per wine). */
  worldScope?: EventWorldScope;
  /** Who may RSVP themselves. Past members / in memoriam are never accepted. */
  invitedMemberTypes?: MemberType[];
  /** Pay by card to confirm the seat; needs a price. */
  requiresPayment?: boolean;
  /** Members with pending annual dues can't RSVP themselves. */
  requiresDuesPaid?: boolean;
  /** Cancelling a paid seat within this many days of the tasting forfeits it. */
  cancellationCutoffDays?: number | null;
}

export interface CreateEventInput extends EventSetupInput {
  title: string;
  theme?: string;
  regionLocation?: string;
  venue?: string;
  eventYear: number;
  eventMonth?: number | null;
  eventDate?: string | null;
  datePrecision?: string;
  isScored?: boolean;
  dressCode?: string;
  price?: number | null;
  capacity?: number | null;
  notes?: string;
  /** Broadcast the "new tasting" announcement to all members on create. */
  announce?: boolean;
  /** Days before the tasting to nudge non-responders, e.g. [14, 3]. */
  reminderDays?: number[];
  /**
   * Create this as a rehearsal: every feature works, but it stays out of the
   * archive, stats and exports, and only the people on its test roster are ever
   * messaged. Flipped later through PUT /events/:id/test-mode (until final).
   */
  isTest?: boolean;
  /** The theme's rules, shown on the RSVP. */
  rules?: string;
  /** Wine quotas created with the tasting (edit later via PUT wine-categories). */
  wineCategories?: WineCategoryInput[];
}

/** A real person added to a test event's comms roster. */
export interface TestParticipant {
  id: string;
  personId: string;
  firstName: string;
  lastName: string;
  email: string | null;
  addedAt: string;
}

export interface UpdateEventInput extends EventSetupInput {
  title?: string;
  theme?: string | null;
  regionLocation?: string | null;
  venue?: string | null;
  eventYear?: number;
  eventMonth?: number | null;
  eventDate?: string | null;
  datePrecision?: string;
  isScored?: boolean;
  dressCode?: string | null;
  price?: number | null;
  capacity?: number | null;
  notes?: string | null;
  reminderDays?: number[];
  rules?: string | null;
}

/** GET /events/:id/rsvp/summary — the v1 fields plus Tier 2's (see `rsvpApi.summary`). */
export type RsvpSummary = RsvpSummaryView;

export interface Registration {
  wineName: string | null;
  labelImageUrl: string | null;
  notes: string | null;
  id: string;
  personId: string;
  firstName: string;
  lastName: string;
  email: string | null;
  status: string;
  guestsCount: number;
  registeredAt: string;
}

export interface ScoreBody {
  wineId: string;
  clientId: string;
  /**
   * Device clock at the moment of the tap. The server keeps the newest write per
   * (wine, person), so a retry can never clobber a later correction.
   */
  clientUpdatedAt?: string;
  sightNose: number | null;
  taste: number | null;
  overall: number | null;
  notes?: string;
}

export interface WinePatch {
  vintage?: number | null;
  producer?: string | null;
  varietal?: string | null;
  regionAva?: string | null;
  vineyard?: string | null;
  wineName?: string | null;
  broughtByPersonId?: string | null;
  revealed?: boolean;
}

export const tastingsApi = {
  list: (status?: string) =>
    api
      .get<{ events: EventRow[] }>(`/events${status ? `?status=${status}` : ''}`)
      .then((r) => r.events),
  get: (id: string) => api.get<{ event: EventRow; wines: WineRow[] }>(`/events/${id}`),
  create: (body: CreateEventInput) =>
    api.post<{ event: EventRow }>('/events', body).then((r) => r.event),
  update: (id: string, body: UpdateEventInput) =>
    api.patch<{ event: EventRow }>(`/events/${id}`, body).then((r) => r.event),
  addWines: (id: string, count: number) =>
    api.post<{ wines: WineRow[] }>(`/events/${id}/wines`, { count }),
  updateWine: (id: string, wineId: string, patch: WinePatch) =>
    api.patch<{ wine: WineRow }>(`/events/${id}/wines/${wineId}`, patch),
  deleteWine: (id: string, wineId: string) => api.del(`/events/${id}/wines/${wineId}`),
  setPhase: (id: string, phase: string) =>
    api.post<{ event: EventRow }>(`/events/${id}/phase`, { phase }),
  live: (id: string, since?: number) => {
    const qs = since != null ? `?since=${since}` : '';
    return api.get<LiveTastingView | null>(`/live/${id}${qs}`);
  },
  deleteEvent: (id: string) => api.del(`/events/${id}`),
  score: (id: string, body: ScoreBody) => api.put<LiveTastingView>(`/live/${id}/scores`, body),
  rsvpSummary: (id: string) => api.get<RsvpSummary>(`/events/${id}/rsvp/summary`),
  rsvp: (
    id: string,
    guestsCount: number,
    details: { wineName?: string; labelImageUrl?: string; notes?: string } = {},
  ) => api.post<{ registration: unknown }>(`/events/${id}/rsvp`, { guestsCount, ...details }),
  cancelRsvp: (id: string) => api.del(`/events/${id}/rsvp`),
  registrations: (id: string) =>
    api
      .get<{ registrations: Registration[] }>(`/events/${id}/registrations`)
      .then((r) => r.registrations),

  // Test-event roster — who a rehearsal is allowed to message.
  testParticipants: (id: string) =>
    api.get<{ data: TestParticipant[] }>(`/events/${id}/test-participants`).then((r) => r.data),
  addTestParticipant: (id: string, personId: string) =>
    api.post<{ ok: true }>(`/events/${id}/test-participants`, { personId }),
  removeTestParticipant: (id: string, personId: string) =>
    api.del(`/events/${id}/test-participants/${personId}`),
};
